import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Combina classes Tailwind sem conflitos.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Categoriza um procedimento (string livre) em 4 tipos de documento.
 * Usado para colorir cards (consulta=teal, receita=violet, exame=sky, atestado=amber).
 */
export type TipoDocumento = 'consulta' | 'receita' | 'exame' | 'atestado';

export function classificarProcedimento(
  dsProcedimento?: string | null,
): TipoDocumento {
  const m = (dsProcedimento || '').toUpperCase();
  if (m.includes('RECEIT')) return 'receita';
  if (m.includes('CONSULT')) return 'consulta';
  if (m.includes('ATESTAD')) return 'atestado';
  return 'exame';
}

/**
 * Categoriza um atributo (anamnese, receita, etc.) pelo nome.
 */
export function classificarAtributo(a: {
  ds_atributo: string;
}): TipoDocumento {
  const s = (a.ds_atributo || '').toUpperCase();
  if (s.includes('RECEIT')) return 'receita';
  if (s.includes('CONSULT') || s.includes('ANAMNESE') || s.includes('EVOLU')) {
    return 'consulta';
  }
  if (s.includes('ATESTAD')) return 'atestado';
  return 'exame';
}

/**
 * Calcula idade a partir de data de nascimento no formato dd/mm/yyyy.
 * Retorna "? anos" se a data for inválida.
 */
export function formatarIdade(dtNascimento?: string | null): string {
  if (!dtNascimento) return '?';
  const partes = dtNascimento.split('/');
  if (partes.length !== 3) return '?';
  const [dia, mes, ano] = partes.map(Number);
  if (!dia || !mes || !ano) return '?';

  const hoje = new Date();
  const nasc = new Date(ano, mes - 1, dia);
  let idade = hoje.getFullYear() - nasc.getFullYear();
  const m = hoje.getMonth() - nasc.getMonth();
  if (m < 0 || (m === 0 && hoje.getDate() < nasc.getDate())) idade--;
  return `${idade} anos`;
}

/**
 * Formata data dd/mm/yyyy para pt-BR legível.
 * Se já vier nesse formato, retorna como está.
 */
export function formatarData(dt?: string | null): string {
  if (!dt) return '?';
  // dt pode ser "16/08/2017" ou "16/08/2017 10:50" ou ISO
  if (dt.includes('/')) return dt;
  try {
    const d = new Date(dt);
    if (isNaN(d.getTime())) return dt;
    return d.toLocaleDateString('pt-BR');
  } catch {
    return dt;
  }
}

/**
 * Decodifica base64 para Uint8Array (browser).
 * Usado para converter bb_valor do atributo em Blob .rtf.
 */
export function base64ToBlob(base64: string, mimeType: string): Blob {
  const bin = atob(base64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mimeType });
}

/**
 * Inicia download de Blob no browser.
 */
export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Sanitiza string para uso como nome de arquivo.
 * Remove acentos, troca espaços por underscore, mantém apenas [A-Za-z0-9_-].
 */
export function sanitizeFilename(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z0-9_-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase()
    .slice(0, 60);
}

/**
 * Formata iniciais do nome (até 2 letras) para avatares.
 */
export function iniciais(nome?: string | null): string {
  if (!nome) return '?';
  return nome
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase();
}
